'use client';
import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

let cookieCache: boolean | null = null;

function canUseCookies(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  if (cookieCache !== null) return cookieCache;
  const k = '__sb_test__';
  document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
  cookieCache = document.cookie.includes(k);
  document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;
  return cookieCache;
}

function fromCookies() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return [];
  return document.cookie
    .split(';')
    .filter(Boolean)
    .map((c) => {
      const idx = c.trim().indexOf('=');
      const name = idx !== -1 ? c.trim().slice(0, idx) : c.trim();
      const value = idx !== -1 ? decodeURIComponent(c.trim().slice(idx + 1)) : '';
      return { name: name.trim(), value };
    })
    .filter((c) => c.name);
}

function fromStorage() {
  try {
    if (typeof window === 'undefined') return [];
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k, value: localStorage.getItem(k) || '' }));
  } catch {
    return [];
  }
}

function setCookie(name: string, value: string, options?: any) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=None; Secure; Partitioned`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
}

function getToken(): string | null {
  return (
    (canUseCookies() ? fromCookies() : fromStorage()).find((c) =>
      c.name.includes('auth-token')
    )?.value ?? null
  );
}

function patchFetch() {
  if (typeof window === 'undefined' || (window as any).__sb_patched__) return;
  (window as any).__sb_patched__ = true;
  const orig = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const token = getToken();
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.href
        : (input as Request).url;
    if (token && (url.startsWith('/') || url.startsWith(window.location.origin))) {
      init = {
        ...(init || {}),
        headers: { ...(init?.headers || {}), 'x-sb-token': token },
      };
    }
    return orig(input, init);
  };
}

export function createClient() {
  if (typeof window !== 'undefined') {
    patchFetch();
  }
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () =>
          typeof window === 'undefined'
            ? []
            : canUseCookies()
            ? fromCookies()
            : fromStorage(),

        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          if (canUseCookies()) {
            cookiesToSet.forEach(({ name, value, options }) =>
              value
                ? setCookie(name, value, options)
                : (document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`)
            );
          } else {
            cookiesToSet.forEach(({ name, value, options }) => {
              try {
                value
                  ? localStorage.setItem(`${PFX}${name}`, value)
                  : localStorage.removeItem(`${PFX}${name}`);
              } catch {}
              if (value) setCookie(name, value, options);
            });
          }
        },
      },
    }
  );
}
